import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { SlipContext } from 'context/slipContext';
import { createSharedSlip } from 'services/api/sportBetsService';
import { SlipEntry } from 'types/SlipEntry';
import { ModernSheet } from 'components/mobile/ModernHome/ModernSheet';
import { sharedSlipLink } from 'components/mobile/ModernHome/BetTogether';
import styles from './ShareSlip.module.css';

type Shared = { code: string; liveLeftOut: boolean };

/**
 * "Share with friends" on the modern bet slip: saves the slip's prematch
 * picks under a short code and shows the code and a link to send. Live picks
 * cannot be shared: their price is only good for a moment.
 */
export const ShareSlip = () => {
  const { t } = useTranslation();
  const { entries } = useContext(SlipContext);
  const [busy, setBusy] = useState(false);
  const [shared, setShared] = useState<Shared | null>(null);
  const close = useCallback(() => setShared(null), []);

  const share = async () => {
    if (busy) return;
    const legs: { fi: number; odd_custom_id: string }[] = [];
    let liveLeftOut = false;
    (entries as SlipEntry[]).forEach((e) =>
      e.bets.forEach((b) => {
        if (e.isLive || b.isLive) {
          liveLeftOut = true;
          return;
        }
        if (b.disabled || b.started || b.odd.custom_id == null) return;
        legs.push({
          fi: Number(e.match.fi),
          odd_custom_id: String(b.odd.custom_id),
        });
      })
    );
    if (legs.length === 0) {
      toast.error(t('modern_share_nothing'));
      return;
    }
    setBusy(true);
    const code = await createSharedSlip(legs);
    setBusy(false);
    if (!code) {
      toast.error(t('modern_share_failed'));
      return;
    }
    setShared({ code, liveLeftOut });
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('modern_share_copied'));
    } catch {
      // No clipboard (http, old browser): the code is on screen to type.
    }
  };

  const send = async (code: string) => {
    const link = sharedSlipLink(code);
    try {
      await navigator.share({
        title: t('modern_bet_together_title'),
        text: t('modern_share_message', { code }),
        url: link,
      });
    } catch {
      // Cancelled by the player.
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={share}
        disabled={busy}
      >
        <svg viewBox="0 0 24 24" aria-hidden>
          <path
            fill="currentColor"
            d="M18 16.1c-.8 0-1.4.3-2 .8l-7.1-4.2c.1-.2.1-.5.1-.7s0-.5-.1-.7L16 7.2c.5.5 1.2.8 2 .8a3 3 0 1 0-3-3c0 .2 0 .5.1.7L8 9.8A3 3 0 1 0 8 14.2l7.1 4.2c-.1.2-.1.4-.1.6a2.9 2.9 0 1 0 3-2.9z"
          />
        </svg>
        {t('modern_share_slip')}
      </button>

      {shared && (
        <ModernSheet title={t('modern_share_title')} close={close}>
          <div className={styles.sheet}>
            <p>{t('modern_share_hint')}</p>
            <div className={styles.code}>{shared.code}</div>
            {shared.liveLeftOut && (
              <p className={styles.warn}>{t('modern_share_live_left_out')}</p>
            )}
            <div className={styles.actions}>
              <button type="button" onClick={() => copy(shared.code)}>
                {t('modern_share_copy')}
              </button>
              <button
                type="button"
                onClick={() => copy(sharedSlipLink(shared.code))}
              >
                {t('modern_share_copy_link')}
              </button>
            </div>
            {typeof navigator.share === 'function' && (
              <button
                type="button"
                className={styles.send}
                onClick={() => send(shared.code)}
              >
                {t('modern_share_send')}
              </button>
            )}
          </div>
        </ModernSheet>
      )}
    </>
  );
};
